import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../../../state'
import * as conversationItem from '../conversation-item'
import { Store } from '../store'

/**
 * Renders a collapsible workspace folder with its nested conversations.
 */
@ngCore.Component({
  imports: [
    conversationItem.Component,
    ds.tag.Component,
    ds.button.Component,
    ds.notice.Component,
    ds.overlay.centered.Component,
    ds.overlay.confirmation.Component,
    ds.layout.widget.Component,
    ds.input.text.Component,
    ds.input.select.multiple.Component,
  ],
  selector: 'atn-workspace-item',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /**
   * Style enum exposed to HTML
   *
   * @see {@link ents.core.display.Style}
   */
  public readonly StyleEnum = ents.core.display.Style

  /** @see state.assistant.conversational.Group */
  public readonly assistantGroup = ngCore.inject(
    state.assistant.conversational.Group,
  )

  /**
   * @see {@link Store}
   */
  public readonly store = ngCore.inject(Store)

  /**
   * Whether the current user is the owner of the workspace. Shared workspaces
   * are read-only for non-owners.
   */
  public readonly isOwner = ngCore.computed(
    () => this.workspaceSg().ownerId === this.store.currentUserId(),
  )

  /**
   * Whether the workspace is shared with other users.
   */
  public readonly isShared = ngCore.computed(
    () => this.workspaceSg().sharedWith.length > 0,
  )

  /**
   * The workspace to display
   */
  public readonly workspaceSg =
    ngCore.input.required<ents.core.agent.Workspace>({
      alias: 'workspace',
    })

  /**
   * Whether the workspace is expanded
   */
  public isExpandedSg = ngCore.signal(true)

  /** @see {@link ds.color.Legacy} */
  public readonly sharedTagStyle = ds.color.Legacy.PredefinedStyle

  /**
   * Create a new conversation in this workspace
   */
  public createConversation(event: Event): void {
    event.stopPropagation()
    this.assistantGroup.conversation.plugins.currentEntityPlugin.storage.actions.putInIdLookup
      .from(
        'DolomiteGeneral.ChatPage.Conversation.Creation.InWorkspace.Clicked',
      )
      .dispatch({ value: undefined })
    this.assistantGroup.workspace.plugins.currentEntityPlugin.storage.actions.putInIdLookup
      .from(
        'DolomiteGeneral.ChatPage.Conversation.Creation.InWorkspace.Clicked',
      )
      .dispatch({ value: this.workspaceSg().id })
  }

  /**
   * Edit a workspace name
   */
  public editWorkspace(event: Event): void {
    event.stopPropagation()
    this.assistantGroup.workspace.plugins.editingPlugin.feature.actions.edit
      .from('DolomiteGeneral.ChatPage.Workspace.Edit.Clicked')
      .dispatch({ id: this.workspaceSg().id, makeCurrent: false })
  }

  /**
   * Mark a workspace for deletion (shows confirmation dialog)
   */
  public deleteWorkspace(event: Event): void {
    event.stopPropagation()
    this.assistantGroup.workspace.plugins.editingPlugin.feature.actions.markForDeletion
      .from('DolomiteGeneral.ChatPage.deleteWorkspace')
      .dispatch({
        id: this.workspaceSg().id,
      })
  }

  /**
   * Confirm deletion of the marked workspace
   */
  public confirmDeleteWorkspace(): void {
    this.assistantGroup.workspace.plugins.editingPlugin.feature.actions.submit
      .from('Confirmation.Footer.Buttons.Confirm.Clicked')
      .dispatch({ delete: true })
  }

  /**
   * Cancel deletion of the marked workspace
   */
  public cancelDeleteWorkspace(): void {
    this.assistantGroup.workspace.plugins.editingPlugin.feature.actions.discard
      .from('Confirmation.Footer.Buttons.Cancel.Clicked')
      .dispatch({ delete: true, skipUnset: true })
  }

  /**
   * Update workspace
   */
  public updateWorkspace(
    updates: Partial<state.assistant.conversational.workspace.Entity>,
  ): void {
    this.assistantGroup.workspace.plugins.editingPlugin.feature.actions.edit
      .from('DolomiteGeneral.ChatPage.Workspace.Name.Inputted')
      .dispatch({
        edit: { input: updates },
        id: this.workspaceSg().id,
        makeCurrent: false,
      })
  }

  /**
   * Save workspace changes
   */
  public saveWorkspace(): void {
    this.assistantGroup.workspace.plugins.editingPlugin.feature.actions.submit
      .from('DolomiteGeneral.ChatPage.Workspace.SaveButton.Clicked')
      .dispatch({
        skipUnset: true,
        update: true,
      })
  }

  /**
   * Cancel workspace edit
   */
  public cancelEditWorkspace(): void {
    this.assistantGroup.workspace.plugins.editingPlugin.feature.actions.discard
      .from('DolomiteGeneral.ChatPage.Workspace.CancelButton.Clicked')
      .dispatch({
        skipUnset: true,
        update: true,
      })
  }
}
